import { createSelector } from 'reselect'

import {
  getAttentionGeoStartDepth,
  getAttentionGroupingAccess,
  type IAttentionOwnerAssignment,
  resolveAttentionLevel,
} from '../../lib'

import {
  selectCockpitAttentionCockpitMode,
  selectCockpitAttentionLevelGrouping,
  selectCockpitAttentionPath,
} from './selectCockpitAttentionDrill'
import { selectCockpitAttentionGrouping } from './selectCockpitAttentionGrouping'
import { selectCockpitAttentionManagerAssignments } from './selectCockpitAttentionManagerAssignments'
import {
  selectCockpitAttentionPartners,
  selectCockpitMultiPartnerAttentionStatuses,
} from './selectCockpitAttentionPartners'
import { selectCockpitAttentionPbmAssignments } from './selectCockpitAttentionPbmAssignments'
import { selectCockpitGeoMappingItems } from './selectCockpitGeoMappingItems'
import { selectCockpitSelectedAttentionStatusBit } from './selectCockpitSelectedAttentionStatusBit'
import { selectCockpitUserAccess } from './selectCockpitUserAccess'
import { selectCockpitAccessLevel } from './selectCockpitUserInfo'

/** Which groupings the reader's role may pick, and which one they start on. */
export const selectCockpitAttentionGroupingAccess = createSelector(
  [selectCockpitUserAccess],
  ({ role }) => getAttentionGroupingAccess(role)
)

/** The status the manager cockpit's drill-down stands inside; `null` on the status list. */
export const selectCockpitAttentionSelectedStatus = createSelector(
  [
    selectCockpitMultiPartnerAttentionStatuses,
    selectCockpitSelectedAttentionStatusBit,
    selectCockpitAttentionCockpitMode,
  ],
  (statuses, selectedBit, cockpitMode) =>
    cockpitMode === 'multiple-partner'
      ? (statuses.find(({ bitValue }) => bitValue === selectedBit) ?? null)
      : null
)

/** Partners behind the picked status, in the order the cockpit lists them. */
export const selectCockpitAttentionStatusPartners = createSelector(
  [selectCockpitAttentionSelectedStatus, selectCockpitAttentionPartners],
  (status, partners) => {
    if (!status) {
      return []
    }

    const raisedIds = new Set(status.partnerIds)

    return partners.filter(({ partnerId }) => raisedIds.has(partnerId))
  }
)

const selectResolveParams = createSelector(
  [
    selectCockpitAttentionGroupingAccess,
    selectCockpitGeoMappingItems,
    selectCockpitAccessLevel,
    selectCockpitAttentionPbmAssignments,
    selectCockpitAttentionManagerAssignments,
  ],
  (access, geoMappingItems, accessLevel, pbmAssignments, managerAssignments) => ({
    access,
    geoMappingItems,
    geoStartDepth: getAttentionGeoStartDepth(accessLevel),
    pbmAssignments,
    managerAssignments,
  })
)

/**
 * The level the manager cockpit's drill-down stands on inside the picked status; `null` on the
 * status list. The first level takes the reader's stored grouping, deeper ones their own pick.
 */
export const selectCockpitAttentionLevel = createSelector(
  [
    selectCockpitAttentionSelectedStatus,
    selectCockpitAttentionStatusPartners,
    selectCockpitAttentionPath,
    selectCockpitAttentionLevelGrouping,
    selectCockpitAttentionGrouping,
    selectResolveParams,
  ],
  (status, partners, path, levelGrouping, storedGrouping, params) =>
    status
      ? resolveAttentionLevel({
          ...params,
          partners,
          path,
          grouping: path.length > 0 ? levelGrouping : storedGrouping,
        })
      : null
)

export interface IAttentionPartnerOwners {
  pbms: IAttentionOwnerAssignment[]
  managers: IAttentionOwnerAssignment[]
}

/** Every PBM and manager of each partner, for the full-screen partner table. */
export const selectCockpitAttentionOwnersByPartner = createSelector(
  [selectCockpitAttentionPbmAssignments, selectCockpitAttentionManagerAssignments],
  (pbmAssignments, managerAssignments) => {
    const owners = new Map<string, IAttentionPartnerOwners>()
    const entry = (partnerId: string) => {
      const existing = owners.get(partnerId)

      if (existing) {
        return existing
      }

      const created: IAttentionPartnerOwners = { pbms: [], managers: [] }
      owners.set(partnerId, created)

      return created
    }
    const add = (list: IAttentionOwnerAssignment[], assignment: IAttentionOwnerAssignment) => {
      if (!list.some(({ ownerId }) => ownerId === assignment.ownerId)) {
        list.push(assignment)
      }
    }

    pbmAssignments.forEach((assignment) => add(entry(assignment.partnerId).pbms, assignment))
    managerAssignments.forEach((assignment) =>
      add(entry(assignment.partnerId).managers, assignment)
    )

    return owners
  }
)
